import {
  DateField,
  TextField,
  // SearchableSelectField,
  SelectField,
  LabelValuePair,
  MaskedField,
} from 'theSource/components';
import { i18n } from 'i18n';
import {
  // MAX_OPTIONS_BEFORE_SEARCH,
  isBenefitSalaryField,
  EMPTY_VALUE_INDICATOR,
  getDisplayValue,
} from 'theSource/utils';
import {
  PrimitiveFieldValue,
  BenefitSalary,
  FormField,
  FormFieldsCategory,
} from 'theSource/types';
import {
  EMAIL_FIELD_NAME,
  SECONDARY_EMAIL_FIELD_NAME,
  CELLPHONE_FIELD_NAME,
  TEXT_FIELD_NAME,
} from './constants';
import autocompleteValues from './autocomplete.json';

const DECIMAL_NUMBER_REGEX = /^\d*\.?\d*$/;

export const getReadonlyField = (field: FormField) => {
  if (isBenefitSalaryField(field.fieldName)) {
    let currentBenefitSalary: BenefitSalary | undefined;

    if (Array.isArray(field.value) && field.value.length > 0) {
      currentBenefitSalary = field.value.reduce(
        (prev: BenefitSalary, current: BenefitSalary) => {
          return prev.orgPlanYearId > current.orgPlanYearId ? prev : current;
        }
      );
    }
    return (
      <>
        <LabelValuePair
          key={field.label}
          label={field.label}
          value={`$ ${currentBenefitSalary?.benefitSalary}`}
        />
      </>
    );
  }

  const displayValue = getDisplayValue(
    field.value as PrimitiveFieldValue,
    field.dataType,
    field.options
  );

  return (
    <LabelValuePair
      greyedOutValue={displayValue === EMPTY_VALUE_INDICATOR}
      key={field.label}
      label={field.label}
      required={field.required}
      value={displayValue}
    />
  );
};

export const getFormField = (field: FormField) => {
  if (!field.editable) {
    return getReadonlyField(field);
  }

  // Dynamically fetch autocomplete value if it exists in autocompleteValues
  const autocompleteValue =
    field.fieldName in autocompleteValues
      ? autocompleteValues[field.fieldName as keyof typeof autocompleteValues]
      : 'on';

  const generalProps = {
    id: field.fieldName,
    key: field.fieldName,
    label: field.label,
    name: field.fieldName,
    ...(autocompleteValue ? { autocomplete: autocompleteValue } : {}),
  };

  switch (field.dataType) {
    case 'BOOLEAN':
      return (
        <ps-checkbox
          required={field.required}
          required-word="required"
          data-testid="checkbox-field"
          {...generalProps}
        />
      );
    case 'DATE':
      return (
        <DateField
          helperText={i18n.t('common:dateHelperText')}
          required={field.required}
          {...generalProps}
        />
      );
    case 'NUMBER':
      return (
        <TextField
          pattern={DECIMAL_NUMBER_REGEX}
          required={field.required}
          inputProps={{ inputMode: 'numeric' }}
          {...generalProps}
        />
      );
    default:
      if (field.fieldName === 'ssn') {
        return (
          <MaskedField
            variant="ssn"
            required={field.required}
            type={field.inputType}
            maxLength={11}
            {...generalProps}
          />
        );
      }
      if (field.fieldName === 'sin') {
        return (
          <MaskedField
            variant="sin"
            required={field.required}
            type={field.inputType}
            maxLength={11}
            {...generalProps}
          />
        );
      }
      if (field.options.length) {
        const emptyValueLabel = `${i18n.t('common:select')} ${field.label}`;

        const selectProps = {
          labelId: field.label,
          options: [{ label: emptyValueLabel, value: '' }, ...field.options],
          required: field.required,
        };

        // if (field.options.length > MAX_OPTIONS_BEFORE_SEARCH) {
        //   return (
        //     <SearchableSelectField
        //       noResultsText={i18n.t('common:noSearchResults')}
        //       searchPlaceholder={i18n.t('common:search')}
        //       {...generalProps}
        //       {...selectProps}
        //     />
        //   );
        // }

        return <SelectField {...generalProps} {...selectProps} />;
      }
      return (
        <TextField
          required={field.required}
          type={field.inputType}
          {...generalProps}
        />
      );
  }
};

export const adjustCommunicationModeValidation = (
  communicationMode: string,
  categories: FormFieldsCategory[]
) => {
  const fields = (categories || []).flatMap((category) => category.fields);
  const email = fields.find(
    (field) =>
      field.fieldName === EMAIL_FIELD_NAME ||
      field.fieldName === SECONDARY_EMAIL_FIELD_NAME
  );
  const phone = fields.find(
    (field) => field.fieldName === CELLPHONE_FIELD_NAME
  );
  const text = fields.find((field) => field.fieldName === TEXT_FIELD_NAME);

  switch (communicationMode) {
    case 'email':
      if (email) {
        email.required = true;
      }
      if (text) {
        text.required = false;
      }
      if (phone) {
        phone.required = false;
      }
      break;

    case 'sms':
      if (phone) {
        phone.required = true;
      }
      if (text) {
        text.required = true;
      }
      if (email) {
        email.required = false;
      }
      break;

    case '':
      if (phone) {
        phone.required = false;
      }
      if (text) {
        text.required = false;
      }
      if (email) {
        email.required = false;
      }
  }
  return categories;
};
